import React, { useEffect, useState } from 'react';
import { RefActiveParticipantsWithAllPlacements } from '../hooks/DatasetHooks';
import * as libCon from '../community-hats-js-library/Constants.js';
import * as locCon from '../LocalConstants';
import { Button, Space, Table } from 'antd';
import { Typography } from 'antd';
import {
  getColorOfExpiry,
  getColorOfLastUpload,
  getColorOfLostDays,
  getTextOfExpiry,
  isAirtableNull,
  isNullOrUndefined,
  isNullOrUndefinedOrEmpty,
} from '../community-hats-js-library/utils/generalFunctions.js';
import {
  formatDistanceGivenDays,
  formatDistanceShort,
  formatMinutes,
  getDaysAgo,
} from '../community-hats-js-library/utils/dateFunctions.js';
import {
  getFinalPercentageFromWearableCoverage,
  SymbolForStatus,
} from '../community-hats-js-library/utils/devicesStatusFunctions';
import { ArrowRightOutlined } from '@ant-design/icons';
import { setValue } from '../store/actions/generalStoreActions';
import { RefSetCurrentPage } from '../hooks/NavigationHooks';
import LoadingDatasets from '../components/LoadingDatasets';
import { RefStoredValue } from '../hooks/GeneralStoreHooks';
import { CustomCircularProgressBar } from '../community-hats-js-library/components/WearableCoverageViewer.js';
import LocationPicker from '../components/LocationPicker.js';
import { FilterComponent } from '../components/FilterComponents.js';
import { RefContextLanguage } from '../providers/LanguageProvider';
import { tMessage } from '../utils/translationFunctions';

const { Text, Title } = Typography;

const SymbolFromActiveDevice = ({ device }) => {
  const i18n = RefContextLanguage();

  if (isNullOrUndefined(device))
    return (
      <SymbolForStatus
        translation={(v) => tMessage(i18n, v)}
        status={libCon.DEVICE_STATUS_INCOMPLETE_DEPLOYMENT}
        message={libCon.MESSAGE_INCOMPLETE_DEPLOYMENT}
      />
    );

  return (
    <SymbolForStatus
      translation={(v) => tMessage(i18n, v)}
      status={device[libCon.ATF_DEVICE_COLLECTION_STATUS]}
      message={device[libCon.ATF_DEVICE_COLLECTION_MESSAGE]}
    />
  );
};

const getGeneralColumns = (i18n) => [
  {
    title: <p>{i18n.t('collectionStatus')}</p>,
    dataIndex: libCon.AT_COLLECTION_STATUS,
    key: 1,
    align: 'center',
    width: 100,
    sorter: (a, b) => a[libCon.AT_COLLECTION_STATUS].localeCompare(b[libCon.AT_COLLECTION_STATUS]),
    render: (val, p) => (
      <SymbolForStatus translation={(v) => tMessage(i18n, v)} status={p[libCon.AT_COLLECTION_STATUS]} message={p[libCon.AT_COLLECTION_MESSAGE]} />
    ),
  },
  {
    title: <p>{i18n.t('area')}</p>,
    dataIndex: libCon.ATF_AREA,
    key: 1,
    align: 'center',
    width: 100,
    render: (val, record) => (
      <p style={{ fontSize: 12, textAlign: 'center', color: getColorOfExpiry(record[libCon.ATF_DAYS_UNTIL_EXPIRE]) }}>
        {val}
      </p>
    ),
  },
  {
    title: i18n.t('names'),
    dataIndex: libCon.ATF_NAME,
    key: 2,
    align: 'center',
    width: 100,
    fixed: 'left',
    render: (val, record) => (
      <p style={{ textAlign: 'center', color: getColorOfExpiry(record[libCon.ATF_DAYS_UNTIL_EXPIRE]) }}>
        {record[libCon.ATF_NAME] + ' ' + record[libCon.ATF_LAST_NAME]}
      </p>
    ),
  },
  {
    title: i18n.t('sewaId'),
    dataIndex: libCon.ATF_SEWA_ID,
    key: 3,
    align: 'center',
    width: 100,
    render: (val, record) => (
      <p style={{ fontSize: 12, textAlign: 'center', color: getColorOfExpiry(record[libCon.ATF_DAYS_UNTIL_EXPIRE]) }}>
        {val}
      </p>
    ),
  },
  {
    title: i18n.t('address'),
    dataIndex: libCon.ATF_ADDRESS,
    key: 4,
    align: 'center',
    width: 100,
    render: (val, record) => (
      <p style={{ fontSize: 12, textAlign: 'center', color: getColorOfExpiry(record[libCon.ATF_DAYS_UNTIL_EXPIRE]) }}>
        {val}
      </p>
    ),
  },
  {
    title: <p>{i18n.t('nextCollection')}</p>,
    dataIndex: libCon.ATF_DAYS_UNTIL_EXPIRE,
    key: 5,
    align: 'center',
    width: 100,
    defaultSortOrder: 'ascend',
    sorter: (a, b) => a[libCon.ATF_DAYS_UNTIL_EXPIRE] - b[libCon.ATF_DAYS_UNTIL_EXPIRE],
    render: (val, record) => (
      <p style={{ textAlign: 'center', color: getColorOfExpiry(record[libCon.ATF_DAYS_UNTIL_EXPIRE]) }}>
        {getTextOfExpiry(val)}
      </p>
    ),
  },
  {
    title: <p>{i18n.t('lastCollection')}</p>,
    dataIndex: libCon.ATF_LATEST_COLLECTION_DATE,
    key: 6,
    align: 'center',
    width: 100,
    sorter: (a, b) => a[libCon.ATF_DAYS_SINCE_LAST_HOUSE_VISIT] - b[libCon.ATF_DAYS_SINCE_LAST_HOUSE_VISIT],
    render: (val, record) => (
      <p style={{ textAlign: 'center', color: getColorOfLastUpload(record[libCon.ATF_DAYS_SINCE_LAST_HOUSE_VISIT]) }}>
        {formatDistanceShort(val)}
      </p>
    ),
  },
  {
    title: <p>{i18n.t('lostData')}</p>,
    dataIndex: libCon.ATF_DAYS_UNTIL_EXPIRE,
    key: 7,
    align: 'center',
    width: 100,
    sorter: (a, b) => a[libCon.ATF_DAYS_UNTIL_EXPIRE] - b[libCon.ATF_DAYS_UNTIL_EXPIRE],
    render: (val, record) => (
      <p style={{ textAlign: 'center', color: getColorOfLostDays(record[libCon.ATF_DAYS_UNTIL_EXPIRE]) }}>
        {val >= 0 ? i18n.t('zeroDays') : formatDistanceGivenDays(val, false)}
      </p>
    ),
  },
  {
    title: <p>{i18n.t('sensorStatus')}</p>,
    dataIndex: libCon.ATF_HAS_ACTIVE_SENSORS,
    key: 8,
    align: 'center',
    width: 100,
    sorter: (a, b) => a[libCon.AT_SENSORS_COLLECTION_STATUS].localeCompare(b[libCon.AT_SENSORS_COLLECTION_STATUS]),
    render: (val, record) => (
      // <div>
      //   <SymbolForStatus
      //     status={record[libCon.AT_SENSORS_COLLECTION_STATUS]}
      //     message={record[libCon.AT_SENSORS_COLLECTION_MESSAGE]}
      //   />
      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
        {record[libCon.AT_SENSORS_COLLECTION_STATUS_LIST].map((s, i) => (
          <SymbolForStatus translation={(v) => tMessage(i18n, v)} status={s} message={record[libCon.AT_SENSORS_COLLECTION_MESSAGE_LIST][i]} />
        ))}
      </div>
    ),

    // </div>
  },
  {
    title: <p>{i18n.t('locationTrackerStatus')}</p>,
    dataIndex: libCon.ATF_HAS_ACTIVE_PHONES,
    key: 9,
    align: 'center',
    width: 80,
    sorter: (a, b) =>
      isNullOrUndefined(a[libCon.ATF_ACTIVE_PHONE])
        ? -1
        : isNullOrUndefined(b[libCon.ATF_ACTIVE_PHONE])
          ? 1
          : a[libCon.ATF_ACTIVE_PHONE][libCon.ATF_DEVICE_COLLECTION_STATUS].localeCompare(
              b[libCon.ATF_ACTIVE_PHONE][libCon.ATF_DEVICE_COLLECTION_STATUS]
            ),
    render: (val, record) => <SymbolFromActiveDevice device={record[libCon.ATF_ACTIVE_PHONE]} />,
  },
  {
    title: <p>{i18n.t('wearableStatus')}</p>,
    dataIndex: libCon.ATF_HAS_ACTIVE_EMAILS,
    key: 10,
    align: 'center',
    width: 80,
    sorter: (a, b) =>
      isNullOrUndefined(a[libCon.ATF_ACTIVE_EMAIL])
        ? -1
        : isNullOrUndefined(b[libCon.ATF_ACTIVE_EMAIL])
          ? 1
          : a[libCon.ATF_ACTIVE_EMAIL][libCon.ATF_DEVICE_COLLECTION_STATUS].localeCompare(
              b[libCon.ATF_ACTIVE_EMAIL][libCon.ATF_DEVICE_COLLECTION_STATUS]
            ),
    render: (val, record) => <SymbolFromActiveDevice device={record[libCon.ATF_ACTIVE_EMAIL]} />,
  },
  {
    title: <p>{i18n.t('wearableCoverage')}</p>,
    dataIndex: libCon.ATF_HAS_ACTIVE_EMAILS,
    key: 10,
    align: 'center',
    width: 80,
    sorter: (a, b) =>
      isNullOrUndefined(a[libCon.ATF_ACTIVE_EMAIL])
        ? -1
        : isNullOrUndefined(b[libCon.ATF_ACTIVE_EMAIL])
          ? 1
          : a[libCon.ATF_ACTIVE_EMAIL][libCon.ATF_WEARABLE_TOTAL_COVERAGE] -
            b[libCon.ATF_ACTIVE_EMAIL][libCon.ATF_WEARABLE_TOTAL_COVERAGE],
    render: (val, record) =>
      !record[libCon.ATF_HAS_ACTIVE_EMAILS] ||
      record[libCon.ATF_ACTIVE_EMAIL][libCon.ATF_DEVICE_COLLECTION_STATUS] !== libCon.DEVICE_STATUS_OK ? (
        '--'
      ) : getDaysAgo(record[libCon.ATF_JOINED_DATE]) < libCon.WEARABLE_COVERAGE_BACK_DAYS ? (
        <p style={{ fontSize: '12px' }}>{i18n.t('placementJustStarted')}</p>
      ) : (
        <CustomCircularProgressBar
          percentage={getFinalPercentageFromWearableCoverage(
            record[libCon.ATF_ACTIVE_EMAIL][libCon.ATF_WEARABLE_TOTAL_COVERAGE]
          )}
          fontSize="12px"
          strokeWidth={10}
          size={32}
          showInfo={true}
        />
      ),
  },
  {
    title: <p>{i18n.t('perceptualSurveyStatus')}</p>,
    dataIndex: libCon.ATF_PERCEPTUAL_SURVEY,
    key: 11,
    align: 'center',
    width: 100,
    sorter: (a, b) =>
      a[libCon.ATF_PERCEPTUAL_SURVEY][libCon.ATF_DEVICE_COLLECTION_STATUS].localeCompare(
        b[libCon.ATF_PERCEPTUAL_SURVEY][libCon.ATF_DEVICE_COLLECTION_STATUS]
      ),
    render: (val, record) => (
      <SymbolForStatus
        translation={(v) => tMessage(i18n, v)}
        status={record[libCon.ATF_PERCEPTUAL_SURVEY][libCon.ATF_DEVICE_COLLECTION_STATUS]}
        message={record[libCon.ATF_PERCEPTUAL_SURVEY][libCon.ATF_DEVICE_COLLECTION_MESSAGE]}
      />
    ),
  },
  {
    title: <p>{i18n.t('intakeFormStatus')}</p>,
    dataIndex: libCon.ATF_ALL_INTAKE_FORMS,
    key: 12,
    align: 'center',
    width: 80,
    sorter: (a, b) =>
      a[libCon.ATF_ALL_INTAKE_FORMS][libCon.ATF_DEVICE_COLLECTION_STATUS].localeCompare(
        b[libCon.ATF_ALL_INTAKE_FORMS][libCon.ATF_DEVICE_COLLECTION_STATUS]
      ),
    render: (val, record) => (
      <SymbolForStatus
        translation={(v) => tMessage(i18n, v)}
        status={record[libCon.ATF_ALL_INTAKE_FORMS][libCon.ATF_DEVICE_COLLECTION_STATUS]}
        message={record[libCon.ATF_ALL_INTAKE_FORMS][libCon.ATF_DEVICE_COLLECTION_MESSAGE]}
      />
    ),
  },
  {
    title: <p>{i18n.t('averageCollectionTime')}</p>,
    dataIndex: libCon.ATF_AVERAGE_COLLECTION_DURATION,
    key: 13,
    align: 'center',
    width: 80,
    sorter: (a, b) =>
      isAirtableNull(a)
        ? -1
        : isAirtableNull(b)
          ? 1
          : a[libCon.ATF_AVERAGE_COLLECTION_DURATION] - b[libCon.ATF_AVERAGE_COLLECTION_DURATION],
    render: (val) => <p>{isNullOrUndefined(val) || isAirtableNull(val) ? '---' : formatMinutes(val / 60)}</p>,
  },
  {
    title: <p>{i18n.t('actionItems')}</p>,
    dataIndex: libCon.ATF_ISSUES_STATUS,
    key: 13,
    align: 'center',
    width: 80,
    sorter: (a, b) =>
      a[libCon.ATF_ISSUES_STATUS][libCon.ATF_DEVICE_COLLECTION_STATUS].localeCompare(
        b[libCon.ATF_ISSUES_STATUS][libCon.ATF_DEVICE_COLLECTION_STATUS]
      ),
    render: (val, record) => (
      <SymbolForStatus
        translation={(v) => tMessage(i18n, v)}
        status={record[libCon.ATF_ISSUES_STATUS][libCon.ATF_DEVICE_COLLECTION_STATUS]}
        message={record[libCon.ATF_ISSUES_STATUS][libCon.ATF_DEVICE_COLLECTION_MESSAGE]}
      />
    ),
  },
];

function DashboardExpiryParticipants() {
  // Navigation
  const setCurrentPage = RefSetCurrentPage();

  // Location
  const location = RefStoredValue(locCon.PROJECT_LOCATION);

  // Participants
  const [status, participants] = RefActiveParticipantsWithAllPlacements();

  const [participantArray, setParticipantArray] = useState([]);

  const filterText = RefStoredValue(locCon.FILTER_TEXT_EXPIRY_PARTICIPANTS);

  // Status
  const [isTableReady, setIsTableReady] = useState(false);

  useEffect(() => {
    setIsTableReady(status !== libCon.STATUS_NOT_STARTED && status !== libCon.LOADING && participants !== null);
  }, [status, participants]);

  useEffect(() => {
    if (participants !== null) {
      let parArray = Object.values(participants);

      // Filter Text
      if (!isNullOrUndefinedOrEmpty(filterText))
        parArray = parArray.filter((ob) =>
          ob[libCon.ATF_ISSUE_SHARE_ID].toUpperCase().includes(filterText.toUpperCase())
        );

      // Filter Location
      if (!isNullOrUndefined(location) && location !== libCon.ATF_ALL)
        parArray = parArray.filter(
          (ob) =>
            libCon.ATF_AREA in ob && ob[libCon.ATF_AREA].map((s) => s.toUpperCase()).includes(location.toUpperCase())
        );

      setParticipantArray(parArray);
    }
  }, [participants, filterText, location]);

  const userType = RefStoredValue(locCon.USER_TYPE);

  const i18n = RefContextLanguage();

  const [columns, setColumns] = useState([]);

  useEffect(() => {
    const goToParticipant = (id) => {
      setValue(locCon.SELECTED_PARTICIPANT, id);
      setCurrentPage(locCon.PAGE_DASHBOARD_SINGLE_COLLECTION_STATUS);
    };

    let localColumns = [
      ...getGeneralColumns(i18n),
      {
        title: i18n.t('goTo'),
        dataIndex: libCon.ID,
        key: 14,
        align: 'center',
        width: 80,
        render: (val) => (
          <Button type="primary" onClick={() => goToParticipant(val)}>
            <ArrowRightOutlined />
          </Button>
        ),
      },
    ];

    if (!isNullOrUndefined(location) && location !== libCon.ATF_ALL) {
      localColumns = localColumns.filter((col) => col.dataIndex !== libCon.ATF_AREA);
    }

    const sensitiveCols = [libCon.ATF_NAME, libCon.ATF_ADDRESS, libCon.ATF_PHONE_NUMBER];

    if (userType !== locCon.USER_TYPE_SEWA)
      localColumns = localColumns.filter((col) => !sensitiveCols.includes(col.dataIndex));

    setColumns(localColumns);
  }, [userType, setCurrentPage, location, i18n]);

  return (
    <div className="mainContainer">
      <Title level={2}>{i18n.t('participantDataAboutToExpire')}</Title>
      <Space direction="vertical" style={{ marginBottom: '10px' }}>
        <LocationPicker />
      </Space>

      <Space direction="vertical">
        <FilterComponent filterId={locCon.FILTER_TEXT_EXPIRY_PARTICIPANTS} />
        <Space direction="horizontal" size={'large'}>
          <Space direction="horizontal">
            <SymbolForStatus translation={(v) => tMessage(i18n, v)} size={26} status={libCon.DEVICE_STATUS_OK} message={libCon.MESSAGE_DATA_UP_TO_DATE} />
            <Text style={{ fontSize: '0.8em', color: 'var(--text-color)' }}>
              {!isTableReady
                ? '--'
                : participantArray.filter((p) => p[libCon.AT_COLLECTION_STATUS] === libCon.DEVICE_STATUS_OK).length}
            </Text>
          </Space>
          <Space direction="horizontal">
            <SymbolForStatus
              translation={(v) => tMessage(i18n, v)}
              size={26}
              status={libCon.DEVICE_STATUS_WAITING_COLLECTION}
              message={libCon.MESSAGE_WAITING_FOR_COLLECTION}
            />
            <Text style={{ fontSize: '0.8em', color: 'var(--text-color)' }}>
              {!isTableReady
                ? '--'
                : participantArray.filter(
                    (p) => p[libCon.AT_COLLECTION_STATUS] === libCon.DEVICE_STATUS_WAITING_COLLECTION
                  ).length}
            </Text>
          </Space>
          <Space direction="horizontal">
            <SymbolForStatus
              translation={(v) => tMessage(i18n, v)}
              size={26}
              status={libCon.DEVICE_STATUS_WAITING_COLLECTION_LOOSING_DATA}
              message={libCon.MESSAGE_WAITING_FOR_COLLECTION_LOOSING_DATA}
            />
            <Text style={{ fontSize: '0.8em', color: 'var(--text-color)' }}>
              {!isTableReady
                ? '--'
                : participantArray.filter(
                    (p) => p[libCon.AT_COLLECTION_STATUS] === libCon.DEVICE_STATUS_WAITING_COLLECTION_LOOSING_DATA
                  ).length}
            </Text>
          </Space>
          <Space direction="horizontal">
            <SymbolForStatus
              translation={(v) => tMessage(i18n, v)}
              size={26}
              status={libCon.DEVICE_STATUS_ERROR_IN_COLLECTION}
              message={libCon.MESSAGE_ERROR_IN_COLLECTION}
            />
            <Text style={{ fontSize: '0.8em', color: 'var(--text-color)' }}>
              {!isTableReady
                ? '--'
                : participantArray.filter(
                    (p) => p[libCon.AT_COLLECTION_STATUS] === libCon.DEVICE_STATUS_ERROR_IN_COLLECTION
                  ).length}
            </Text>
          </Space>
        </Space>
      </Space>

      <div style={{ maxWidth: '98vw', marginTop: '20px' }}>
        {!isTableReady ? (
          <LoadingDatasets />
        ) : status === libCon.ERROR ? (
          <div>{i18n.t('error')}</div>
        ) : (
          <Table
            dataSource={participantArray}
            columns={columns}
            size="small"
            scroll={{ x: 'max-content', y: window.innerHeight * 0.65 }}
            pagination={{ pageSize: 50 }}
          />
        )}
      </div>
    </div>
  );
}

export default DashboardExpiryParticipants;
